// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type {
  CmtsComplaintDetailVm,
  CmtsComplaintRecordVm,
} from "../../view-models/complaintViewModels.js";
import type {
  CmtsConfigurationDraftVm,
  CmtsConfigurationVersionVm,
} from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import {
  decideCmtsExport,
  createCmtsExportAuditPayload,
} from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import {
  normalizeTypedReportCode,
  reportCodeFromPathname,
} from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import {
  activeRows,
  assertRepositoryOk,
  businessValueOptionOf,
  complaintQuery,
  createOrUpdateMasterData,
  dataOr,
  deactivateMasterData,
  diagnosticsOf,
  emptyBackendDashboard,
  errorOf,
  invalidateAdminArea,
  loadMasterOptions,
  masterCrudDisplayName,
  optionOf,
  reactivateMasterData,
  tenantId,
  useAdminDependencyData,
  useCmtsRouterSearch,
  type CmtsConnectedContainerProps,
} from "../domainContainerSupport.js";
import { CmtsMasterDataAdminPage } from "@modules/pages/cmtsMasterDataAdminPage.js";
import { CmtsReferenceAdminPage } from "@modules/pages/cmtsReferenceAdminPage.js";
import { CmtsSeverityPage } from "@modules/pages/cmtsSeverityPage.js";
import { CmtsPriorityPage } from "@modules/pages/cmtsPriorityPage.js";
import { CmtsComplaintCategoryPage } from "@modules/pages/cmtsComplaintCategoryPage.js";
import { CmtsComplaintSubcategoryPage } from "@modules/pages/cmtsComplaintSubcategoryPage.js";
import { CmtsComplaintSourcePage } from "@modules/pages/cmtsComplaintSourcePage.js";
import { CmtsReferenceDataPage } from "@modules/pages/cmtsReferenceDataPage.js";
import { ReferenceTypePage } from "@modules/pages/referenceTypePage.js";

export function CmtsSeverityContainer() {
  return <CmtsSeverityPage />;
}
export function CmtsPriorityContainer() {
  return <CmtsPriorityPage />;
}
export function CmtsComplaintCategoryContainer() {
  return <CmtsComplaintCategoryPage />;
}
export function CmtsComplaintSubcategoryContainer() {
  return <CmtsComplaintSubcategoryPage />;
}
export function CmtsComplaintSourceContainer() {
  return <CmtsComplaintSourcePage />;
}
export function CmtsReferenceDataContainer() {
  return <CmtsReferenceDataPage />;
}
export function ReferenceTypeContainer() {
  return <ReferenceTypePage />;
}

export function CmtsMasterDataAdminContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const routeSegment = location.pathname.split("/").filter(Boolean).at(-1);
  const entity = cmtsMasterDataEntityForPathSegment(routeSegment);
  const masterType = entity?.masterType;
  const key = ["cmts", "master-data", masterType ?? "all", t];
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      if (masterType) {
        return runtime.repositories.masterData.list(masterType, t);
      }
      const primaryTypes = [
        "complaint-type",
        "workflow-status",
        "rca-method",
        "root-cause-category",
        "capa-action-type",
        "verification-method",
        "defect-mode",
        "failure-mode",
        "departments",
        "sites",
        "products",
        "evidence-type",
      ];
      const results = await Promise.all(
        primaryTypes.map(async (type) => {
          try {
            const res = await runtime.repositories.masterData.list(type, t);
            return dataOr(res, []);
          } catch {
            return [];
          }
        }),
      );
      return results.flat();
    },
    retry: 1,
  });
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      const targetMasterType = String(
        entity?.masterType ??
          payload.group ??
          payload.masterType ??
          masterType ??
          "complaint-type",
      );
      return createOrUpdateMasterData(
        runtime,
        targetMasterType,
        payload,
        t,
        masterCrudDisplayName(payload),
      );
    },
    onSuccess: async () =>
      invalidateAdminArea(queryClient, t, [
        key,
        ["cmts", "complaint-create-master-options", t],
        ["cmts", "reports", t],
        ["cmts", "dashboard"],
      ]),
  });
  const deactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const targetMasterType = String(
        entity?.masterType ??
          row.group ??
          row.masterType ??
          masterType ??
          "complaint-type",
      );
      return deactivateMasterData(
        runtime,
        targetMasterType,
        row,
        "Dependency checked deactivation from enterprise master data administration",
        t,
      );
    },
    onSuccess: async () =>
      invalidateAdminArea(queryClient, t, [
        key,
        ["cmts", "complaint-create-master-options", t],
        ["cmts", "reports", t],
        ["cmts", "dashboard"],
      ]),
  });
  const reactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const targetMasterType = String(
        entity?.masterType ??
          row.group ??
          row.masterType ??
          masterType ??
          "complaint-type",
      );
      return reactivateMasterData(
        runtime,
        targetMasterType,
        row,
        "Dependency checked reactivation from enterprise master data administration",
        t,
      );
    },
    onSuccess: async () =>
      invalidateAdminArea(queryClient, t, [
        key,
        ["cmts", "complaint-create-master-options", t],
        ["cmts", "reports", t],
        ["cmts", "dashboard"],
      ]),
  });

  const queryRows = useMemo(() => {
    if (!query.data) return [];
    if (Array.isArray(query.data))
      return query.data as readonly Record<string, unknown>[];
    return dataOr(
      query.data as RepositoryResult<readonly unknown[]>,
      [],
    ) as readonly Record<string, unknown>[];
  }, [query.data]);

  const queryResultEnvelope = useMemo(() => {
    if (query.data && !Array.isArray(query.data)) return query.data;
    return undefined;
  }, [query.data]);

  return (
    <CmtsMasterDataAdminPage
      {...(masterType ? { masterType } : {})}
      rows={queryRows}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        queryResultEnvelope,
        saveMutation.data,
        deactivate.data,
        reactivate.data,
      )}
      dependencyData={dependencyData}
      onUpsert={async (payload) => {
        assertRepositoryOk(
          await saveMutation.mutateAsync(payload),
          "Administration save",
        );
      }}
      onDeactivate={async (row) => {
        assertRepositoryOk(
          await deactivate.mutateAsync(row),
          "Administration deactivate",
        );
      }}
      onReactivate={async (row) => {
        assertRepositoryOk(
          await reactivate.mutateAsync(row),
          "Administration reactivate",
        );
      }}
      onRefresh={async () => {
        await query.refetch();
      }}
    />
  );
}

export function CmtsReferenceAdminContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "reference-admin", t];
  const rowsQuery = useQuery({
    queryKey: key,
    queryFn: () => runtime.repositories.masterData.list("referenceData", t),
    retry: 1,
  });
  const referenceCategories = useQuery({
    queryKey: ["cmts", "reference", "categories", t],
    queryFn: () => runtime.repositories.reference.categories(t),
    retry: 1,
  });
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      createOrUpdateMasterData(
        runtime,
        String(payload.group ?? payload.masterType ?? "referenceData"),
        payload,
        t,
        masterCrudDisplayName(payload),
      ),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const deactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      deactivateMasterData(
        runtime,
        String(row.group ?? row.masterType ?? "referenceData"),
        row,
        "Dependency checked deactivation from reference administration",
        t,
      ),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const reactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      reactivateMasterData(
        runtime,
        String(row.group ?? row.masterType ?? "referenceData"),
        row,
        "Dependency checked reactivation from reference administration",
        t,
      ),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  return (
    <CmtsReferenceAdminPage
      referenceHealth={[
        ...(dataOr(rowsQuery.data, []) as readonly Record<string, unknown>[]),
        ...(dataOr(referenceCategories.data, []) as readonly Record<
          string,
          unknown
        >[]),
      ]}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        rowsQuery.data,
        referenceCategories.data,
        saveMutation.data,
        deactivate.data,
        reactivate.data,
      )}
      dependencyData={dependencyData}
      onUpsert={async (payload) => {
        assertRepositoryOk(
          await saveMutation.mutateAsync(payload),
          "Administration save",
        );
      }}
      onDeactivate={async (row) => {
        assertRepositoryOk(
          await deactivate.mutateAsync(row),
          "Administration deactivate",
        );
      }}
      onReactivate={async (row) => {
        assertRepositoryOk(
          await reactivate.mutateAsync(row),
          "Administration reactivate",
        );
      }}
      onRefresh={async () => {
        await Promise.all([rowsQuery.refetch(), referenceCategories.refetch()]);
      }}
    />
  );
}
